import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Menu, Grid } from 'antd'
import IntlMessage from '../util-components/IntlMessage'
import Icon from '../util-components/Icon'
import navigationConfig from 'configs/NavigationConfig'
import { connect, useDispatch, useSelector } from 'react-redux'
import { SIDE_NAV_LIGHT, NAV_TYPE_SIDE } from 'constants/ThemeConstant'
import utils from 'utils'
import { onMobileNavToggle } from 'store/slices/themeSlice'
import {
  ADMIN_TYPE,
  AUTH_ROLES,
  AUTH_TYPE,
  PUBLIC_NAV_KEYS,
  SUBADMIN_TYPE,
} from 'constants/AuthConstant'

const { useBreakpoint } = Grid

const setLocale = (localeKey, isLocaleOn = true) =>
  isLocaleOn ? <IntlMessage id={localeKey} /> : localeKey.toString()

const setDefaultOpen = (key) => {
  const keyList = []
  let keyString = ''
  if (key) {
    const arr = key.split('-')
    arr.forEach((elm, index) => {
      keyString = index === 0 ? elm : `${keyString}-${elm}`
      keyList.push(keyString)
    })
  }
  return keyList
}

const MenuItem = ({ title, menuTitleLines, path, closeMobileNav }) => (
  <>
    <span className={menuTitleLines?.length ? 'side-nav-menu-title-multiline' : undefined}>
      {menuTitleLines?.length
        ? menuTitleLines.map((line, index) => (
            <span className="side-nav-menu-title-line" key={`${line}-${index}`}>
              {line}
            </span>
          ))
        : setLocale(title)}
    </span>
    {path && <Link onClick={closeMobileNav} to={path} />}
  </>
)

const menuIcon = (icon) => (icon ? <Icon type={icon} className="nav-menu-icon" /> : null)

const isSubAdminType = (type) =>
  type === SUBADMIN_TYPE || type === 'Sub-admin'

const isPublicNav = (key) => PUBLIC_NAV_KEYS.includes(key)

const hasRoleView = (key, roles) =>
  isPublicNav(key) ||
  Boolean(roles?.[key]?.view ?? roles?.[key]?.viewOnly)

const canAccessNav = (nav, type, roles) => {
  if (
    nav.isGroupTitle ||
    isPublicNav(nav.key) ||
    type === ADMIN_TYPE ||
    hasRoleView(nav.key, roles)
  ) {
    return true
  }
  if (!isSubAdminType(type)) {
    return false
  }
  if (nav.isMenu && nav.submenu?.length) {
    return nav.submenu.some((child) => canAccessNav(child, type, roles))
  }
  return hasRoleView(nav.key, roles)
}

const getSideNavMenuItem = (navItem, type, roles, closeMobileNav) =>
  navItem
    .map((nav) => {
      if (canAccessNav(nav, type, roles)) {
        if (nav.submenu.length === 0 && !nav.isPage) return null
      } else {
        return null
      }

      return {
        key: nav.key,
        className: nav.menuTitleLines?.length
          ? 'side-nav-menu-item-multiline'
          : undefined,
        icon: nav.isGroupTitle ? undefined : menuIcon(nav.icon),
        label: (
          <MenuItem
            title={nav.title}
            menuTitleLines={nav.menuTitleLines}
            closeMobileNav={closeMobileNav}
            {...(nav.isGroupTitle ? {} : { path: nav.path })}
          />
        ),
        ...(nav.isGroupTitle ? { type: 'group' } : {}),
        ...(nav.submenu.length > 0
          ? {
              children: getSideNavMenuItem(nav.submenu, type, roles, closeMobileNav),
            }
          : {}),
      }
    })
    .filter(Boolean)

const getTopNavMenuItem = (navItem) =>
  navItem.map((nav) => ({
    key: nav.key,
    icon: nav.isGroupTitle ? undefined : menuIcon(nav.icon),
    label: (
      <MenuItem
        title={nav.title}
        {...(nav.isGroupTitle ? {} : { path: nav.path })}
      />
    ),
    ...(nav.submenu.length > 0
      ? { children: getTopNavMenuItem(nav.submenu) }
      : {}),
  }))

const SideNavContent = (props) => {
  const {
    routeInfo,
    hideGroupTitle,
    sideNavTheme = SIDE_NAV_LIGHT,
    onMobileNavToggle,
    navCollapsed,
  } = props
  const dispatch = useDispatch()
  const rolesFromStore = useSelector((state) => state.auth.roles)
  const typeFromStore = useSelector((state) => state.auth.type)

  const type = typeFromStore || localStorage.getItem(AUTH_TYPE)
  const roles = useMemo(() => {
    if (rolesFromStore && Object.keys(rolesFromStore).length > 0) {
      return rolesFromStore
    }
    try {
      return JSON.parse(localStorage.getItem(AUTH_ROLES) || '{}')
    } catch {
      return {}
    }
  }, [rolesFromStore])

  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes('lg')
  const closeMobileNav = () => {
    if (isMobile) {
      dispatch(onMobileNavToggle(false))
    }
  }

  const menuItems = useMemo(
    () => getSideNavMenuItem(navigationConfig, type, roles, closeMobileNav),
    [type, roles, closeMobileNav]
  )

  const menuItemsFiltered = menuItems?.filter(
    (item) => item.children?.length !== 0
  )

  return (
    <Menu
      mode="inline"
      inlineCollapsed={navCollapsed}
      theme={sideNavTheme === SIDE_NAV_LIGHT ? 'light' : 'dark'}
      style={{ height: '100%', borderInlineEnd: 0 }}
      defaultSelectedKeys={[routeInfo?.key]}
      defaultOpenKeys={setDefaultOpen(routeInfo?.key)}
      className={hideGroupTitle ? 'hide-group-title' : ''}
      items={menuItemsFiltered}
    />
  )
}

const TopNavContent = () => {
  const topNavColor = useSelector((state) => state.theme.topNavColor)
  const menuItems = useMemo(() => getTopNavMenuItem(navigationConfig), [])

  return (
    <Menu mode="horizontal" style={{ backgroundColor: topNavColor }} items={menuItems} />
  )
}

const MenuContent = (props) =>
  props.type === NAV_TYPE_SIDE ? (
    <SideNavContent {...props} />
  ) : (
    <TopNavContent />
  )

const mapStateToProps = ({ theme }) => ({
  sideNavTheme: theme.sideNavTheme,
  topNavColor: theme.topNavColor,
})

export default connect(mapStateToProps, { onMobileNavToggle })(MenuContent)
